import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { CheckCircle2, Loader2, XCircle } from 'lucide-react'
import { api } from '@/lib/api'
import { money } from '@/lib/money'
import Button from '@/components/ui/Button'
import Card from '@/components/ui/Card'
import PageHeader from '@/components/ui/PageHeader'

interface OrderPaymentStatus {
  status: 'pending' | 'paid' | 'failed'
  amount: number
  order_id: number | null
  order_number: string | null
  checkout_url: string | null
  paid_at: string | null
}

const POLL_INTERVAL_MS = 2000
const POLL_ATTEMPTS = 15

/**
 * Where the gateway sends the customer back to after a direct order payment.
 *
 * Same rules as the wallet top-up return page: the browser almost always
 * arrives before the gateway's webhook does, so this polls the payment rather
 * than assuming success, and says so plainly if confirmation has not arrived
 * by the time we stop asking.
 */
export default function OrderPaymentReturn() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [params] = useSearchParams()
  const [status, setStatus] = useState<OrderPaymentStatus | null>(null)
  const [timedOut, setTimedOut] = useState(false)

  const paymentId = params.get('payment_id') ?? sessionStorage.getItem('mpg_pending_order_payment')

  useEffect(() => {
    if (!paymentId) {
      setTimedOut(true)
      return
    }

    let attempts = 0
    let cancelled = false

    const poll = async () => {
      if (cancelled) return
      try {
        const { data } = await api.get<OrderPaymentStatus>(`/orders/payments/${paymentId}`)
        if (cancelled) return
        setStatus(data)
        if (data.status === 'paid' || data.status === 'failed') {
          sessionStorage.removeItem('mpg_pending_order_payment')
          void queryClient.invalidateQueries({ queryKey: ['orders'] })
          return
        }
      } catch {
        // Keep polling: a transient error here should not look like a failed
        // payment when the money may well have been taken.
      }

      attempts += 1
      if (attempts >= POLL_ATTEMPTS) {
        setTimedOut(true)
        return
      }
      window.setTimeout(poll, POLL_INTERVAL_MS)
    }

    void poll()

    return () => {
      cancelled = true
    }
  }, [paymentId, queryClient])

  const paid = status?.status === 'paid'
  const failed = status?.status === 'failed'

  return (
    <div>
      <PageHeader title="Order payment" subtitle="Confirming your payment with the gateway" />
      <Card>
        <div className="py-8 text-center">
          {paid ? (
            <>
              <CheckCircle2 className="mx-auto size-10 text-status-success" />
              <p className="mt-3 text-lg font-semibold">Order confirmed</p>
              <p className="mt-1 text-sm text-muted">
                {money(status?.amount)} received for order {status?.order_number}. Production is scheduled and a
                confirmation email is on its way.
              </p>
            </>
          ) : failed ? (
            <>
              <XCircle className="mx-auto size-10 text-status-danger" />
              <p className="mt-3 text-lg font-semibold">Payment not completed</p>
              <p className="mt-1 text-sm text-muted">
                Nothing was charged. Your order is saved and waiting - you can complete the payment from the order
                page whenever you are ready.
              </p>
            </>
          ) : timedOut ? (
            <>
              <Loader2 className="mx-auto size-10 text-muted" />
              <p className="mt-3 text-lg font-semibold">Still waiting on the bank</p>
              <p className="mt-1 text-sm text-muted">
                This can take a few minutes. If money left your account, the order confirms on its own as soon as
                the gateway tells us - there is no need to pay again.
              </p>
            </>
          ) : (
            <>
              <Loader2 className="mx-auto size-10 animate-spin text-brand-500" />
              <p className="mt-3 text-lg font-semibold">Confirming your payment...</p>
              <p className="mt-1 text-xs text-muted">Please do not pay again while this finishes.</p>
            </>
          )}

          <div className="mt-6 flex justify-center gap-2">
            {status?.order_id ? (
              <Button onClick={() => navigate(`/account/orders/${status.order_id}`)}>View order</Button>
            ) : (
              <Button onClick={() => navigate('/account/orders')}>My orders</Button>
            )}
            {(failed || timedOut) && status?.checkout_url && !paid && (
              <Button variant="outline" onClick={() => (window.location.href = status.checkout_url!)}>
                Try again
              </Button>
            )}
          </div>
        </div>
      </Card>
    </div>
  )
}
